import { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  getCalibreSlugMappings,
  addCalibreSlugMapping,
  deleteCalibreSlugMapping,
  type CalibreSlugMappings,
} from '../../services/firebase';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface SlugGroup {
  slug: string;
  inputs: string[];
}

export function CalibreSlugMappingsModal({ isOpen, onClose }: Props) {
  const [mappings, setMappings] = useState<CalibreSlugMappings>({});
  const [loading, setLoading] = useState(false);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [newSlug, setNewSlug] = useState('');
  const [newInput, setNewInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadMappings = async () => {
    setLoading(true);
    try {
      const data = await getCalibreSlugMappings();
      setMappings(data);
    } catch {
      toast.error('Failed to load slug mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOpen) loadMappings(); }, [isOpen]);

  // Group by slug
  const groups: SlugGroup[] = (() => {
    const map = new Map<string, string[]>();
    Object.entries(mappings).forEach(([input, slug]) => {
      const list = map.get(slug) || [];
      list.push(input);
      map.set(slug, list);
    });
    return Array.from(map.entries())
      .map(([slug, inputs]) => ({ slug, inputs: inputs.sort() }))
      .sort((a, b) => a.slug.localeCompare(b.slug));
  })();

  const filtered = searchTerm
    ? groups.filter(g => g.slug.includes(searchTerm.toLowerCase()) || g.inputs.some(i => i.toLowerCase().includes(searchTerm.toLowerCase())))
    : groups;

  const handleAddMapping = async () => {
    const input = newInput.trim();
    const slug = (editingSlug || newSlug).trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    if (!input || !slug) { toast.error('Both fields are required'); return; }
    setAdding(true);
    try {
      await addCalibreSlugMapping(input, slug);
      setMappings((prev) => ({ ...prev, [input]: slug }));
      setNewInput('');
      if (!editingSlug) setNewSlug('');
      toast.success(`Added: "${input}" → ${slug}`);
    } catch { toast.error('Failed to add mapping'); }
    finally { setAdding(false); }
  };

  const handleDeleteInput = async (input: string) => {
    try {
      await deleteCalibreSlugMapping(input);
      setMappings((prev) => { const n = { ...prev }; delete n[input]; return n; });
      toast.success(`Removed: "${input}"`);
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='Calibre Slug Mappings' size='lg'>
      <p className='text-sm text-gray-500 mb-3'>
        Map calibre names to lookup slugs. Multiple names can map to the same slug.
      </p>

      {/* Search */}
      <input
        type='text'
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder='Search slugs or names...'
        className='w-full border rounded-lg px-3 py-1.5 text-sm mb-3'
      />

      {/* Slug list or editing view */}
      {editingSlug ? (
        <div className='border rounded-lg p-4 space-y-3'>
          <div className='flex items-center justify-between'>
            <div>
              <h3 className='font-medium text-gray-900'>{editingSlug}</h3>
              <a href={`https://calibercorner.com/${editingSlug}/`} target='_blank' rel='noopener noreferrer' className='text-xs text-blue-500 hover:underline'>
                View lookup page →
              </a>
            </div>
            <Button variant='secondary' size='sm' onClick={() => setEditingSlug(null)}>Back to list</Button>
          </div>

          {/* Current mapped values */}
          <div className='space-y-1'>
            <p className='text-xs font-medium text-gray-500 uppercase'>Mapped Names</p>
            {groups.find(g => g.slug === editingSlug)?.inputs.map((input) => (
              <div key={input} className='flex items-center justify-between bg-gray-50 rounded px-3 py-1.5'>
                <span className='text-sm text-gray-800'>{input}</span>
                <button onClick={() => handleDeleteInput(input)} className='text-red-400 hover:text-red-600 text-xs'>✕</button>
              </div>
            ))}
          </div>

          {/* Add another name to this slug */}
          <div className='flex gap-2'>
            <input
              type='text'
              value={newInput}
              onChange={(e) => setNewInput(e.target.value)}
              placeholder='Add another name for this slug...'
              className='flex-1 border rounded px-3 py-1.5 text-sm'
              onKeyDown={(e) => e.key === 'Enter' && handleAddMapping()}
            />
            <Button size='sm' onClick={handleAddMapping} disabled={adding || !newInput.trim()}>
              {adding ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Add new slug + mapping */}
          <div className='flex items-end gap-2 mb-3 p-3 bg-gray-50 rounded-lg'>
            <div className='flex-1'>
              <label className='block text-xs font-medium text-gray-600 mb-1'>Name</label>
              <input type='text' value={newInput} onChange={(e) => setNewInput(e.target.value)} placeholder='e.g., ETA 7750' className='w-full border rounded px-3 py-1.5 text-sm' />
            </div>
            <span className='text-gray-400 text-sm pb-1.5'>→</span>
            <div className='flex-1'>
              <label className='block text-xs font-medium text-gray-600 mb-1'>Slug</label>
              <input type='text' value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder='e.g., eta-valjoux-caliber-7750' className='w-full border rounded px-3 py-1.5 text-sm' />
            </div>
            <Button size='sm' onClick={handleAddMapping} disabled={adding || !newInput.trim() || !newSlug.trim()}>
              {adding ? '...' : 'Add'}
            </Button>
          </div>

          {/* Scrollable slug list */}
          {loading ? (
            <div className='text-center py-8 text-gray-500 text-sm'>Loading...</div>
          ) : filtered.length === 0 ? (
            <div className='text-center py-8 text-gray-400 text-sm'>
              {searchTerm ? 'No matches' : 'No mappings yet'}
            </div>
          ) : (
            <div className='border rounded-lg max-h-80 overflow-y-auto'>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 sticky top-0'>
                  <tr>
                    <th className='text-left px-3 py-2 font-medium text-gray-600'>Slug</th>
                    <th className='text-left px-3 py-2 font-medium text-gray-600'>Mapped Names</th>
                    <th className='w-16' />
                  </tr>
                </thead>
                <tbody className='divide-y'>
                  {filtered.map((g) => (
                    <tr key={g.slug} className='hover:bg-gray-50 cursor-pointer' onClick={() => setEditingSlug(g.slug)}>
                      <td className='px-3 py-2 font-mono text-xs text-blue-600'>{g.slug}</td>
                      <td className='px-3 py-2 text-gray-700'>
                        {g.inputs.slice(0, 3).join(', ')}
                        {g.inputs.length > 3 && <span className='text-gray-400'> +{g.inputs.length - 3} more</span>}
                      </td>
                      <td className='px-2 py-2 text-right'>
                        <span className='text-xs text-gray-400'>{g.inputs.length} name{g.inputs.length !== 1 ? 's' : ''}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className='text-xs text-gray-400 mt-2'>
            {groups.length} slug{groups.length !== 1 ? 's' : ''} • {Object.keys(mappings).length} total mappings
          </div>
        </>
      )}
    </Modal>
  );
}
